import { Button } from '@/components/ui';
import { formatDate } from '@/lib/utils';
import { useTranslation } from '@kgc/i18n';
import { useState } from 'react';
import { useWizardStore } from '../wizard-store';
import { useLastRentalClone } from './use-last-rental-clone';

/**
 * rental/02 — „Mint múltkor" klón-banner (v3-parity, FE-A4 placement).
 *
 * Az equipment-step tetején a partner legutóbbi bérléséből kínál 1-kattintásos
 * újra-kitöltést. A klón-logika (fetch + gép-ID-feloldás + hydrate) a közös
 * `useLastRentalClone` hookban él — ugyanaz, mint a PartnerStep gomb (REUSE,
 * nem rebuild). A copy measured-honest: a `LastRentalCart` csak ID-ket ad, így
 * a banner dátumot + darabszámot mutat; a részletes gép/csomag-nevek az
 * Ismétlés UTÁN töltődnek be a step-2 listába.
 *
 * - Nincs kiválasztott partner / nincs korábbi bérlés → REJTVE (nincs zaj).
 * - „Ismétlés" → applyClone (store hydrate). „Üres bérlés" → banner elvetése.
 */
export function RentalLastRentalBanner() {
  const { t } = useTranslation();
  const partner = useWizardStore(s => s.partner);
  const partnerId = partner?.id ?? null;
  const { cart, busy, applyClone } = useLastRentalClone(partnerId);
  // A dismiss CSAK az aktuális partnerre szól — ha a felhasználó másik partnerre
  // vált, a banner újra megjelenik (a sticky-dismiss bug elkerülése).
  const [dismissedPartnerId, setDismissedPartnerId] = useState<string | null>(null);
  const dismissed = partnerId !== null && dismissedPartnerId === partnerId;

  if (!cart || dismissed) return null;

  return (
    <div
      data-testid="clone-rental-banner"
      className="flex items-center gap-4 border-b-2 border-primary/40 bg-primary/5 px-6 py-3"
    >
      <div
        aria-hidden
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-xl text-primary-foreground"
      >
        ↻
      </div>
      <div className="min-w-0 flex-1">
        <div
          data-testid="clone-rental-banner-title"
          className="text-[11px] font-bold uppercase tracking-wide text-primary"
        >
          {t('rental.clone.banner.label')} · {formatDate(cart.period.startDate)}
        </div>
        <div
          data-testid="clone-rental-banner-summary"
          className="truncate text-sm font-semibold leading-tight text-gray-900 dark:text-white"
        >
          {t('rental.clone.banner.summary', {
            equipment: cart.items.length,
            package: cart.packages.length,
          })}
        </div>
        <p className="mt-0.5 text-[11px] text-muted-foreground">
          {t('rental.clone.banner.helper')}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <Button
          type="button"
          size="sm"
          disabled={busy}
          onClick={() => void applyClone()}
          data-testid="clone-rental-banner-apply"
        >
          {t('rental.clone.banner.apply')}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => setDismissedPartnerId(partnerId)}
          data-testid="clone-rental-banner-dismiss"
        >
          {t('rental.clone.banner.dismiss')}
        </Button>
      </div>
    </div>
  );
}
